/* eslint-disable react-hooks/static-components */
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, ArrowUpDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { useJobCostList } from '@/hooks/use-payroll';
import { useOrgCurrency } from '@/hooks/use-settings';
import { formatMoney } from '@/lib/currency';

type SortKey = 'project_name' | 'labor_cost' | 'expense_cost' | 'purchase_order_cost' | 'per_diem_cost' | 'mileage_cost' | 'total_cost' | 'revenue' | 'profit' | 'margin_percent';

export function JobCostingListPage() {
  const orgCurrency = useOrgCurrency();
  const { data: projects, isLoading } = useJobCostList();
  const [sortKey, setSortKey] = useState<SortKey>('project_name');
  const [sortAsc, setSortAsc] = useState(true);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const sorted = useMemo(() => {
    if (!projects) return [];
    const items = [...projects];
    items.sort((a, b) => {
      let aVal: string | number = a[sortKey] ?? '';
      let bVal: string | number = b[sortKey] ?? '';
      if (sortKey !== 'project_name') {
        aVal = parseFloat(String(aVal)) || 0;
        bVal = parseFloat(String(bVal)) || 0;
      }
      if (aVal < bVal) return sortAsc ? -1 : 1;
      if (aVal > bVal) return sortAsc ? 1 : -1;
      return 0;
    });
    return items;
  }, [projects, sortKey, sortAsc]);

  const fmt = (val: string | null) => {
    if (val === null || val === undefined) return '-';
    const n = parseFloat(val);
    if (isNaN(n)) return val;
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const poCost = (row: { purchase_order_cost?: string; has_excluded_po_costs?: boolean }) => (
    <>
      {money(row.purchase_order_cost ?? '0')}
      {row.has_excluded_po_costs && (
        <span className="block text-xs text-amber-700">+ other currency, not in total</span>
      )}
    </>
  );
  const money = (val: string | null) =>
    val === null ? '-' : formatMoney(val, orgCurrency);

  const marginColor = (val: string | null) => {
    if (val === null || val === undefined) return '';
    const n = parseFloat(val);
    if (isNaN(n)) return '';
    if (n > 0) return 'text-green-600 font-semibold';
    if (n < 0) return 'text-red-600 font-semibold';
    return '';
  };

  const SortHeader = ({ label, field }: { label: string; field: SortKey }) => (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 whitespace-nowrap"
      onClick={() => handleSort(field)}
    >
      <span className="flex items-center gap-1">
        {label}
        <ArrowUpDown className="h-3 w-3" />
      </span>
    </th>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Job Costing</h1>
        <p className="text-gray-600 mt-1">
          Project cost analysis and profitability tracking
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Project Cost Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading job cost data...</div>
          ) : sorted.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <DollarSign className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No project cost data available</p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full min-w-[900px]">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <SortHeader label="Project" field="project_name" />
                      <SortHeader label="Labor" field="labor_cost" />
                      <SortHeader label="Expenses" field="expense_cost" />
                      <SortHeader label="Purchase Orders" field="purchase_order_cost" />
                      <SortHeader label="Per Diem" field="per_diem_cost" />
                      <SortHeader label="Mileage" field="mileage_cost" />
                      <SortHeader label="Total Cost" field="total_cost" />
                      <SortHeader label="Revenue" field="revenue" />
                      <SortHeader label="Profit" field="profit" />
                      <SortHeader label="Margin %" field="margin_percent" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {sorted.map((row) => (
                      <tr key={row.project_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <Link
                            to={`/payroll/job-costing/${row.project_id}`}
                            className="text-blue-600 hover:underline font-medium"
                          >
                            {row.project_name}
                          </Link>
                          <p className="text-xs text-gray-500">{row.project_number}</p>
                        </td>
                        <td className="px-4 py-3 text-sm">{money(row.labor_cost)}</td>
                        <td className="px-4 py-3 text-sm">{money(row.expense_cost)}</td>
                        <td className="px-4 py-3 text-sm">{poCost(row)}</td>
                        <td className="px-4 py-3 text-sm">{money(row.per_diem_cost)}</td>
                        <td className="px-4 py-3 text-sm">{money(row.mileage_cost)}</td>
                        <td className="px-4 py-3 text-sm font-medium">{money(row.total_cost)}</td>
                        <td className="px-4 py-3 text-sm">{money(row.revenue)}</td>
                        <td className={`px-4 py-3 text-sm ${marginColor(row.profit)}`}>
                          {money(row.profit)}
                        </td>
                        <td className={`px-4 py-3 text-sm ${marginColor(row.margin_percent)}`}>
                          {row.margin_percent !== null ? `${fmt(row.margin_percent)}%` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile card list */}
              <div className="lg:hidden space-y-3 px-4 py-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Sort by</span>
                  <Select
                    value={`${sortKey}-${sortAsc ? 'asc' : 'desc'}`}
                    onValueChange={(v) => {
                      const [key, dir] = v.split('-') as [SortKey, string];
                      setSortKey(key);
                      setSortAsc(dir === 'asc');
                    }}
                  >
                    <SelectTrigger className="w-48 min-h-[44px]" size="sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="project_name-asc">Project A–Z</SelectItem>
                      <SelectItem value="project_name-desc">Project Z–A</SelectItem>
                      <SelectItem value="labor_cost-desc">Labor ↓</SelectItem>
                      <SelectItem value="labor_cost-asc">Labor ↑</SelectItem>
                      <SelectItem value="expense_cost-desc">Expenses ↓</SelectItem>
                      <SelectItem value="expense_cost-asc">Expenses ↑</SelectItem>
                      <SelectItem value="per_diem_cost-desc">Per Diem ↓</SelectItem>
                      <SelectItem value="per_diem_cost-asc">Per Diem ↑</SelectItem>
                      <SelectItem value="mileage_cost-desc">Mileage ↓</SelectItem>
                      <SelectItem value="mileage_cost-asc">Mileage ↑</SelectItem>
                      <SelectItem value="total_cost-desc">Total Cost ↓</SelectItem>
                      <SelectItem value="total_cost-asc">Total Cost ↑</SelectItem>
                      <SelectItem value="revenue-desc">Revenue ↓</SelectItem>
                      <SelectItem value="revenue-asc">Revenue ↑</SelectItem>
                      <SelectItem value="profit-desc">Profit ↓</SelectItem>
                      <SelectItem value="profit-asc">Profit ↑</SelectItem>
                      <SelectItem value="margin_percent-desc">Margin ↓</SelectItem>
                      <SelectItem value="margin_percent-asc">Margin ↑</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {sorted.map((row) => (
                  <Card key={row.project_id}>
                    <CardContent className="p-4">
                      <Link
                        to={`/payroll/job-costing/${row.project_id}`}
                        className="text-blue-600 hover:underline font-medium min-h-[44px] inline-flex items-center"
                      >
                        {row.project_name}
                      </Link>
                      <p className="text-xs text-gray-500 mb-3">{row.project_number}</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                        <span className="text-gray-500">Labor</span>
                        <span className="text-right">{money(row.labor_cost)}</span>
                        <span className="text-gray-500">Expenses</span>
                        <span className="text-right">{money(row.expense_cost)}</span>
                        <span className="text-gray-500">Purchase Orders</span>
                        <span className="text-right">{poCost(row)}</span>
                        <span className="text-gray-500">Per Diem</span>
                        <span className="text-right">{money(row.per_diem_cost)}</span>
                        <span className="text-gray-500">Mileage</span>
                        <span className="text-right">{money(row.mileage_cost)}</span>
                        <span className="text-gray-500 font-medium">Total Cost</span>
                        <span className="text-right font-medium">{money(row.total_cost)}</span>
                        <span className="text-gray-500">Revenue</span>
                        <span className="text-right">{money(row.revenue)}</span>
                        <span className="text-gray-500">Profit</span>
                        <span className={`text-right ${marginColor(row.profit)}`}>{money(row.profit)}</span>
                        <span className="text-gray-500">Margin</span>
                        <span className={`text-right ${marginColor(row.margin_percent)}`}>
                          {row.margin_percent !== null ? `${fmt(row.margin_percent)}%` : '-'}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
